import React, { FC, useContext } from 'react';
import { useMutation } from '@apollo/client';
import { Link } from 'gatsby';
import { UserContext } from '../../context/userContext';
import { ProfileContext } from '../../context/profileContext';
import { LOG_OUT } from '../../apollo/mutations';
import { homePageTitle } from '../Utils/environment';
import {
  CartIcon,
  HomeIcon,
  CalendarIcon,
  BrainIcon,
  Logo,
  MindBlownIcon,
} from '../SvgIcon';
import { siteMetaQuery } from '../../staticQueries';
interface NavMenuProps {}

const NavMenu: FC<NavMenuProps> = () => {
  const iconColor: string = 'text-primary';
  const { title: businessName } = siteMetaQuery();
  const navMenuItems = [
    {
      link: '/catalog',
      title: 'Lessons, Classes and Events',
      icon: <CalendarIcon height={5} width={5} />,
    },
    {
      link: '/checkout',
      title: 'Shopping Cart & Checkout',
      icon: <CartIcon height={5} width={5} />,
    },
    {
      link: '/about',
      title: `The ${businessName} Story`,
      icon: (
        <Logo
          height={25}
          width={20}
          colorOne="currentColor"
          colorTwo="currentColor"
        />
      ),
    },
    // Also update AuthMessages.tsx to uncomment link to posts.
    // {
    //   link: '/posts',
    //   title: 'Posts & Media',
    //   icon: <MindBlownIcon height={5} width={5} />,
    // },
    {
      link: '/glossary',
      title: 'Q&A-infused Glossary',
      icon: <BrainIcon height={5} width={5} />,
    },
    {
      link: '/help',
      title: 'Help Topics',
      icon: <BrainIcon height={5} width={5} />,
    },
  ];
  return (
    <ul
      tabIndex={1}
      className="mt-3 z-1 p-2 shadow menu menu-sm dropdown-content bg-base-100 rounded-box w-80"
    >
      {navMenuItems.map((menu, index) => (
        <li key={index} className="hover:text-secondary">
          <Link to={menu.link}>
            <div className="flex items-center">
              {menu.icon}
              <span className="ml-3">{menu.title}</span>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
};
const ThemeSwitch: FC = () => {
  return (
    <label className="swap swap-rotate w-full justify-start itmes-center group">
      {/* this hidden checkbox controls the state */}
      <input
        type="checkbox"
        className="theme-controller"
        value="sunset-light"
      />

      {/* sun icon */}

      <svg
        className="swap-off h-5 w-5 fill-primary"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
      >
        <path d="M5.64,17l-.71.71a1,1,0,0,0,0,1.41,1,1,0,0,0,1.41,0l.71-.71A1,1,0,0,0,5.64,17ZM5,12a1,1,0,0,0-1-1H3a1,1,0,0,0,0,2H4A1,1,0,0,0,5,12Zm7-7a1,1,0,0,0,1-1V3a1,1,0,0,0-2,0V4A1,1,0,0,0,12,5ZM5.64,7.05a1,1,0,0,0,.7.29,1,1,0,0,0,.71-.29,1,1,0,0,0,0-1.41l-.71-.71A1,1,0,0,0,4.93,6.34Zm12,.29a1,1,0,0,0,.7-.29l.71-.71a1,1,0,1,0-1.41-1.41L17,5.64a1,1,0,0,0,0,1.41A1,1,0,0,0,17.66,7.34ZM21,11H20a1,1,0,0,0,0,2h1a1,1,0,0,0,0-2Zm-9,8a1,1,0,0,0-1,1v1a1,1,0,0,0,2,0V20A1,1,0,0,0,12,19ZM18.36,17A1,1,0,0,0,17,18.36l.71.71a1,1,0,0,0,1.41,0,1,1,0,0,0,0-1.41ZM12,6.5A5.5,5.5,0,1,0,17.5,12,5.51,5.51,0,0,0,12,6.5Zm0,9A3.5,3.5,0,1,1,15.5,12,3.5,3.5,0,0,1,12,15.5Z" />
      </svg>

      {/* moon icon */}
      <svg
        className="swap-on h-5 w-5 fill-accent"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
      >
        <path d="M21.64,13a1,1,0,0,0-1.05-.14,8.05,8.05,0,0,1-3.37.73A8.15,8.15,0,0,1,9.08,5.49a8.59,8.59,0,0,1,.25-2A1,1,0,0,0,8,2.36,10.14,10.14,0,1,0,22,14.05,1,1,0,0,0,21.64,13Zm-9.5,6.69A8.14,8.14,0,0,1,7.08,5.22v.27A10.15,10.15,0,0,0,17.22,15.63a9.79,9.79,0,0,0,2.1-.22A8.11,8.11,0,0,1,12.14,19.73Z" />
      </svg>
      <span className="group-hover:text-secondary ml-8 text-lg">Theme</span>
    </label>
  );
};
const UserMenu: FC = () => {
  const { state, dispatch } = useContext(UserContext);
  const { isLoggedIn } = state;
  const { logOut } = useContext(ProfileContext);
  const [handleSignOut, { loading, error, client }] = useMutation(LOG_OUT, {
    onCompleted: () => {
      dispatch({ type: 'LOG_OUT' });
      client.resetStore();
    },
  });
  const userMenuItems = [
    {
      link: '/webapp',
      title: isLoggedIn ? homePageTitle : 'Sign In',
      icon: <HomeIcon height={5} width={5} />,
    },
  ];
  return (
    <ul
      tabIndex={1}
      className="mt-3 z-1 p-2 shadow shadow-md menu menu-sm dropdown-content bg-base-100 rounded-box w-60"
    >
      {userMenuItems.map((menu, index) => (
        <li key={index} className="hover:text-secondary">
          <Link to={menu.link}>
            <div className="flex items-center">
              {menu.icon}
              <span className="ml-3 text-lg">{menu.title}</span>
            </div>
          </Link>
        </li>
      ))}
      {/* If I want to add theme switching later */}
      {/* <li className="flex flex-row group">
        <ThemeSwitch />
      </li> */}
      {isLoggedIn && (
        <li
          className="btn btn-secondary btn-outline btn-xs mt-3 w-full"
          onClick={() => {
            // handleSignOut();
            logOut();
          }}
        >
          {loading ? 'Signing out...' : 'Sign Out'}
        </li>
      )}
    </ul>
  );
};
export { NavMenu, UserMenu };
